import React, { useState, useEffect, useRef } from 'react';
import {
    fetchCategoryIds, fetchCategoryName, fetchItemInfo,
    listFolderImages, uploadItemImage,
    addPortfolioItem, updatePortfolioItem, deletePortfolioItems,
    addCategory, renameCategory, deleteCategoryAndContents, updateCategoryCount,
} from '../../bridge';
import UpgradeModal from './UpgradeModal';
import ManagedServiceBanner from './ManagedServiceBanner';

const FREE_CATEGORIES = 2;
const PAID_CATEGORIES = 9;
const FREE_ITEMS = 2;
const PAID_ITEMS = 3;

// ── Single portfolio item card ────────────────────────────────────────────────
const PortfolioItemCard = ({ item, onSave, onDelete }) => {
    const [name, setName] = useState(item.item_name || '');
    const [description, setDescription] = useState(item.item_description || '');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleSave = async () => { setSaving(true); await onSave(item.imageIndex, name, description); setSaving(false); };
    const handleDelete = async () => { setDeleting(true); await onDelete(item.imageIndex); };

    return (
        <div className="item-card">
            <img src={item.url} alt={name || 'Portfolio item'} className="item-card-image" />
            <div className="item-card-body">
                <input className="item-card-field" value={name} onChange={e => setName(e.target.value)} placeholder="Project / item name" />
                <textarea className="item-card-field textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description of the work" style={{ minHeight: 72 }} />
                <div className="item-card-actions">
                    <button className="small-btn primary" onClick={handleSave} disabled={saving || deleting}>{saving ? '…' : 'Save'}</button>
                    <button className="small-btn danger" onClick={handleDelete} disabled={saving || deleting}>{deleting ? '…' : 'Delete'}</button>
                </div>
            </div>
        </div>
    );
};

// ── Single portfolio category card ────────────────────────────────────────────
const PortfolioCategoryCard = ({ category, email, isSubscribed, onDelete }) => {
    const fileInputRef = useRef(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryName, setCategoryName] = useState(category.name);
    const [editingName, setEditingName] = useState(false);
    const [nameInput, setNameInput] = useState(category.name);
    const [nameSaving, setNameSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => { loadItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadItems = async () => {
        setLoading(true);
        const images = await listFolderImages(email, category.id);
        const withInfo = await Promise.all(images.map(async img => {
            const info = await fetchItemInfo({ userEmail: email, categoryId: category.id, imageIndex: img.name });
            return { imageIndex: img.name, url: img.url, item_name: info?.item_name || '', item_description: info?.item_description || '' };
        }));
        setItems(withInfo);
        setLoading(false);
    };

    const handleAddItemClick = () => {
        if (items.length >= FREE_ITEMS && !isSubscribed) { setShowUpgrade(true); return; }
        if (items.length >= PAID_ITEMS) return;
        fileInputRef.current.click();
    };

    const handleImagePick = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const filename = await uploadItemImage(email, category.id, file);
        if (filename) {
            await addPortfolioItem(email, category.id, filename, '', '');
            const url = `https://yeybluihiblysywmazen.supabase.co/storage/v1/object/public/images/${email}/${category.id}/${filename}`;
            setItems(prev => [...prev, { imageIndex: filename, url, item_name: '', item_description: '' }]);
        }
        setUploading(false);
        e.target.value = '';
    };

    const handleSaveItem = async (imageIndex, name, description) => {
        await updatePortfolioItem(email, category.id, imageIndex, name, description);
        setItems(prev => prev.map(i => i.imageIndex === imageIndex ? { ...i, item_name: name, item_description: description } : i));
    };

    const handleDeleteItem = async (imageIndex) => {
        await deletePortfolioItems(email, category.id, [imageIndex]);
        setItems(prev => prev.filter(i => i.imageIndex !== imageIndex));
    };

    const handleSaveName = async () => {
        setNameSaving(true);
        await renameCategory(email, category.id, nameInput.trim() || categoryName);
        setCategoryName(nameInput.trim() || categoryName);
        setEditingName(false);
        setNameSaving(false);
    };

    const itemLimit = isSubscribed ? PAID_ITEMS : FREE_ITEMS;

    return (
        <>
            {showUpgrade && <UpgradeModal email={email} onDismiss={() => setShowUpgrade(false)} />}
            <div className="category-card">
                <div className="category-card-header">
                    {editingName ? (
                        <>
                            <input className="category-name-input" value={nameInput} onChange={e => setNameInput(e.target.value)} autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setNameInput(categoryName); } }}
                            />
                            <div className="category-header-actions">
                                <button className="hdr-icon-btn hdr-icon-btn-cancel" title="Cancel" onClick={() => { setEditingName(false); setNameInput(categoryName); }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                                <button className="hdr-icon-btn hdr-icon-btn-save" title="Save" onClick={handleSaveName} disabled={nameSaving}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <span className="category-name-display">{categoryName}</span>
                            <div className="category-header-actions">
                                <span style={{ fontSize: 12, color: '#9C8677' }}>{items.length}/{itemLimit}</span>
                                <button className="hdr-icon-btn hdr-icon-btn-edit" title="Rename" onClick={() => setEditingName(true)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </button>
                                {deleteConfirm ? (
                                    <>
                                        <button className="small-btn danger" onClick={() => onDelete(category.id)}>Confirm</button>
                                        <button className="small-btn" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                                    </>
                                ) : (
                                    <button className="hdr-icon-btn hdr-icon-btn-cancel" title="Delete section" onClick={() => setDeleteConfirm(true)}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div className="category-body">
                    {loading ? <p className="manage-empty">Loading items…</p> : items.length === 0 ? <p className="manage-empty">No items yet. Add one below.</p> : (
                        <div className="items-grid">
                            {items.map(item => <PortfolioItemCard key={item.imageIndex} item={item} onSave={handleSaveItem} onDelete={handleDeleteItem} />)}
                        </div>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImagePick} />
                    <button
                        className="small-btn success"
                        onClick={handleAddItemClick}
                        disabled={uploading || items.length >= PAID_ITEMS}
                        title={items.length >= PAID_ITEMS ? `Maximum ${PAID_ITEMS} items reached` : ''}
                    >
                        {uploading ? 'Uploading…' : `+ Add item${!isSubscribed ? ` (${items.length}/${FREE_ITEMS} free)` : ''}`}
                    </button>
                </div>
            </div>
        </>
    );
};

// ── Main PortfolioManager ─────────────────────────────────────────────────────
const PortfolioManager = ({ email, isSubscribed }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [adding, setAdding] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => { loadCategories(); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCategories = async () => {
        setLoading(true);
        const ids = await fetchCategoryIds(email);
        if (!ids || ids.length === 0) { setCategories([]); setLoading(false); return; }
        const withNames = await Promise.all(ids.map(async ({ category_id }) => {
            const name = await fetchCategoryName({ userEmail: email, categoryId: category_id });
            return { id: category_id, name: name || `Section ${category_id}` };
        }));
        setCategories(withNames);
        setLoading(false);
    };

    const handleAddCategoryClick = () => {
        if (categories.length >= FREE_CATEGORIES && !isSubscribed) { setShowUpgrade(true); return; }
        if (categories.length >= PAID_CATEGORIES) return;
        setShowAddForm(true);
    };

    const handleAddCategory = async () => {
        const name = newCategoryName.trim();
        if (!name) return;
        setAdding(true);
        const newId = categories.length === 0 ? 1 : Math.max(...categories.map(c => c.id)) + 1;
        const ok = await addCategory(email, newId, name);
        if (ok) {
            await updateCategoryCount(email, categories.length + 1);
            setCategories(prev => [...prev, { id: newId, name }]);
        }
        setNewCategoryName('');
        setShowAddForm(false);
        setAdding(false);
    };

    const handleDeleteCategory = async (categoryId) => {
        await deleteCategoryAndContents(email, categoryId);
        const updated = categories.filter(c => c.id !== categoryId);
        setCategories(updated);
        await updateCategoryCount(email, updated.length);
    };

    const categoryLimit = isSubscribed ? PAID_CATEGORIES : FREE_CATEGORIES;

    if (loading) return <div className="manage-loading">Loading portfolio…</div>;

    return (
        <>
            {showUpgrade && <UpgradeModal email={email} onDismiss={() => setShowUpgrade(false)} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {categories.length === 0 && <p className="manage-empty">No portfolio sections yet. Add your first one below.</p>}
                {categories.map(cat => (
                    <PortfolioCategoryCard key={cat.id} category={cat} email={email} isSubscribed={isSubscribed} onDelete={handleDeleteCategory} />
                ))}
                {showAddForm ? (
                    <div className="manage-card">
                        <h2 className="manage-card-title">New section</h2>
                        <div className="add-category-form">
                            <input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="Section name (e.g. Web Design)" autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setShowAddForm(false); }}
                            />
                            <button className="small-btn primary" onClick={handleAddCategory} disabled={adding || !newCategoryName.trim()}>{adding ? 'Adding…' : 'Add'}</button>
                            <button className="small-btn" onClick={() => { setShowAddForm(false); setNewCategoryName(''); }}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <button
                        className="manage-add-btn"
                        onClick={handleAddCategoryClick}
                        disabled={categories.length >= PAID_CATEGORIES}
                    >
                        {`+ Add section${!isSubscribed ? ` (${categories.length}/${categoryLimit} free)` : ''}`}
                    </button>
                )}
                <ManagedServiceBanner email={email} />
            </div>
        </>
    );
};

export default PortfolioManager;
